import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { Mail, Download, Trash2, RefreshCw, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('auth_token') || '';

export default function AdminNewsletterPage() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      setSubscribers(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const exportCSV = () => {
    const csv = ['Email,Source,Subscribed At', ...subscribers.map(s => `${s.email},${s.source || 'website'},${s.subscribed_at || ''}`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'newsletter-subscribers.csv'; a.click();
  };

  return (
    <AdminLayout title="Newsletter" breadcrumb="Subscribers">
      <div style={{ maxWidth: 900 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 20, fontWeight: 800, color: '#07121f', letterSpacing: '-.05em', margin: '0 0 4px' }}>
              Newsletter Subscribers
            </h1>
            <p style={{ fontSize: 13, color: 'var(--op-muted)', margin: 0 }}>{subscribers.length} subscriber{subscribers.length !== 1 ? 's' : ''} total</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#fff', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--op-muted)', cursor: 'pointer' }}>
              <RefreshCw size={12} /> Refresh
            </button>
            <button onClick={exportCSV} data-testid="export-csv-btn" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#081D30', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              <Download size={12} /> Export CSV
            </button>
          </div>
        </div>

        <div style={{ background: '#fff', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 12, overflow: 'hidden' }} data-testid="subscribers-table">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--op-muted)' }}>Loading…</div>
          ) : subscribers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--op-muted)' }}>
              <Users size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
              <p>No subscribers yet</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F8F6F0', borderBottom: '1px solid rgba(221,212,197,0.5)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--op-muted)' }}>Email</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--op-muted)' }}>Source</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--op-muted)' }}>Subscribed</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((sub, i) => (
                  <tr key={sub.id || i} style={{ borderBottom: '1px solid rgba(221,212,197,0.3)' }} data-testid="subscriber-row">
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Mail size={14} color="var(--op-teal)" />
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#07121f' }}>{sub.email}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'rgba(0,140,134,.1)', color: '#008C86' }}>
                        {sub.source || 'website'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--op-muted)' }}>
                      {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
